import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import { CONTACT_COPY_DEFAULTS } from './content/pageCopyDefaults';
import './contact-page.css';

function ContactForm({ contact }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFeedback({ type: '', text: '' });
    try {
      const r = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, message }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setFeedback({ type: 'danger', text: d.error || 'Could not send message.' });
        return;
      }
      setFeedback({ type: 'success', text: d.message || 'Thank you — we will reply soon.' });
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
    } catch {
      setFeedback({ type: 'danger', text: 'Could not send message. Try WhatsApp instead.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="contact-form-shell">
      <span className="contact-form-shell__badge">Write to us</span>
      <h2 id="contact-form-heading" className="contact-form-shell__title">
        {contact.form_title}
      </h2>
      <p className="contact-form-shell__subtitle">{contact.form_subtitle}</p>
      <form onSubmit={onSubmit} noValidate aria-labelledby="contact-form-heading">
        {feedback.text ? (
          <div className={`alert alert-${feedback.type} py-2 small mb-3`} role="alert">
            {feedback.text}
          </div>
        ) : null}
        <div className="row g-3">
          <div className="col-md-6">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-name">
                Name <span className="checkout-field__req">*</span>
              </label>
              <input
                id="contact-name"
                className="form-control checkout-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={busy}
                autoComplete="name"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-email">
                Email <span className="checkout-field__req">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                className="form-control checkout-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={busy}
                autoComplete="email"
              />
            </div>
          </div>
          <div className="col-12">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-phone">
                Phone
              </label>
              <input
                id="contact-phone"
                type="tel"
                className="form-control checkout-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
                autoComplete="tel"
              />
            </div>
          </div>
          <div className="col-12">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-message">
                Message <span className="checkout-field__req">*</span>
              </label>
              <textarea
                id="contact-message"
                className="form-control checkout-input"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                disabled={busy}
                placeholder="Tell us about custom orders, bulk gifting, or any question…"
              />
            </div>
          </div>
        </div>
        <button type="submit" className="btn btn-primary store-cta rounded-pill px-4 mt-3" disabled={busy}>
          {busy ? 'Sending…' : contact.form_submit}
        </button>
      </form>
    </div>
  );
}

function ContactHero({ site, contact }) {
  return (
    <header className="contact-hero-banner">
      <div className="contact-hero-banner__inner">
        <p className="contact-hero-banner__eyebrow">{contact.eyebrow}</p>
        <h1 className="contact-hero-banner__title">{contact.hero_title}</h1>
        <p className="contact-hero-banner__subtitle handwritten">{contact.hero_subtitle}</p>
        <div className="contact-hero-banner__actions">
          <a
            href={site.whatsapp_href}
            className="btn btn-primary btn-lg rounded-pill px-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="contact-hero-banner__wa-icon" aria-hidden="true">
              💬
            </span>
            {contact.hero_whatsapp_button}
          </a>
          <Link to="/shop" className="btn btn-outline-light btn-lg rounded-pill px-4">
            {contact.hero_shop_button}
          </Link>
        </div>
      </div>
    </header>
  );
}

function ContactChannels({ site, contact }) {
  const cards = contact.cards || CONTACT_COPY_DEFAULTS.cards;

  const channels = [
    {
      key: 'phone',
      card: cards[0],
      href: site.tel_href,
      action: site.phone_display,
      external: false,
    },
    {
      key: 'email',
      card: cards[1],
      href: site.email_href,
      action: site.email,
      external: false,
    },
    {
      key: 'location',
      card: cards[2],
      href: site.location_line
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.location_line)}`
        : null,
      action: site.location_line,
      external: true,
    },
    {
      key: 'whatsapp',
      card: cards[3],
      href: site.whatsapp_href,
      action: cards[3]?.link_label || 'Chat with us',
      external: true,
    },
  ];

  return (
    <section aria-labelledby="contact-methods-heading">
      <div className="contact-section-head">
        <h2 id="contact-methods-heading" className="contact-section-head__title">
          {contact.reach_title}
        </h2>
        <p className="contact-section-head__lead">{contact.reach_subtitle}</p>
      </div>
      <div className="contact-channel-grid">
        {channels.map((ch) => (
          <ContactChannelCard key={ch.key} channel={ch} />
        ))}
      </div>
    </section>
  );
}

function ContactChannelCard({ channel }) {
  const { card, href, action, external } = channel;
  if (!card?.title) return null;

  const inner = (
    <>
      <span className="contact-channel-card__icon" aria-hidden="true">
        {card.icon}
      </span>
      <div className="contact-channel-card__body">
        <h3 className="contact-channel-card__title">{card.title}</h3>
        <p className="contact-channel-card__desc">{card.desc}</p>
        {action ? (
          <span className="contact-channel-card__action">
            {action}
            {href ? (
              <span className="contact-channel-card__arrow" aria-hidden="true">
                {' '}
                →
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </>
  );

  if (!href) {
    return <div className="contact-channel-card contact-channel-card--static">{inner}</div>;
  }

  return (
    <a
      href={href}
      className="contact-channel-card"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {inner}
    </a>
  );
}

function ContactHours({ site, contact }) {
  return (
    <aside className="contact-hours-panel" aria-labelledby="contact-hours-heading">
      <h2 id="contact-hours-heading" className="contact-hours-panel__title">
        {contact.hours_title}
      </h2>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">Monday – Friday</span>
        <span className="contact-hours-panel__time">{site.hours_mon_fri}</span>
      </div>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">Saturday</span>
        <span className="contact-hours-panel__time">{site.hours_sat}</span>
      </div>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">Sunday</span>
        <span className="contact-hours-panel__time">{site.hours_sun}</span>
      </div>
      {site.hours_note ? <p className="contact-hours-panel__note">{site.hours_note}</p> : null}
    </aside>
  );
}

function ContactStudioBand({ site }) {
  const instagram = String(site.instagram_url || '').trim();
  const location = String(site.location_line || '').trim();
  const madeIn = String(site.store_copy?.footer_made_in || 'Handcrafted in India').trim();
  const hasLinks = location || instagram || site.whatsapp_href;

  if (!hasLinks) return null;

  return (
    <section className="contact-studio-band" aria-label="Studio location and social">
      <div className="contact-studio-band__inner">
        {location ? (
          <div className="contact-studio-band__pin">
            <span className="contact-studio-band__pin-icon" aria-hidden="true">
              📍
            </span>
            <span>
              {location}
              {madeIn ? ` · ${madeIn}` : ''}
            </span>
          </div>
        ) : null}
        <div className="contact-studio-band__links">
          {location ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Maps
            </a>
          ) : null}
          {instagram ? (
            <a
              href={instagram}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </a>
          ) : null}
          {site.whatsapp_href ? (
            <a
              href={site.whatsapp_href}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { settings: site } = useSiteSettings();
  const contact = { ...CONTACT_COPY_DEFAULTS, ...(site.page_copy?.contact || {}) };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact-page">
      <PageSeoHelmet page="contact" />

      <ContactHero site={site} contact={contact} />

      <div className="container store-page__container contact-main">
        <div className="contact-layout">
          <aside className="contact-layout__aside">
            <ContactChannels site={site} contact={contact} />
            <ContactHours site={site} contact={contact} />
          </aside>
          <div className="contact-layout__form">
            <ContactForm contact={contact} />
          </div>
        </div>
      </div>

      <ContactStudioBand site={site} />
    </div>
  );
}

export default Contact;
